/**
 * What a project's status means for the person looking at it — TEN-04.
 *
 * Every status had a pill and nothing else. "failed" is the one that
 * matters: the schema calls it "provisioning failed; compensation ran",
 * which means the resources were already torn down and there is nothing
 * left to repair. `resume` refuses it - only `paused` resumes - so the
 * recovery is to delete the project and create another.
 *
 * None of that was written anywhere a customer looks. They saw a red word
 * and a workspace full of pages that would not work, and no way to tell
 * whether waiting would help.
 */

export type ProjectStatus =
  | 'provisioning' | 'active' | 'suspended' | 'paused' | 'failed' | 'deleting' | 'deleted'

export type StatusNote = {
  tone: 'info' | 'warn'
  /** The state, in the words somebody would use. */
  title: string
  /** What it means, and what to do. Never "try again" when nothing retries. */
  body: string
}

export function statusNote(status: ProjectStatus): StatusNote | null {
  switch (status) {
    case 'active':
      return null

    case 'provisioning':
      return {
        tone: 'info',
        title: 'Being created',
        body: 'Its database, storage and endpoint are being set up. This usually takes under a '
          + 'minute. The pages here will start working as it finishes — nothing needs doing.',
      }

    case 'failed':
      return {
        tone: 'warn',
        title: 'Creating this project did not finish',
        body: 'Everything it had started to create was cleaned up, so nothing is left behind and '
          + 'nothing is being charged for. It cannot be resumed — there is nothing to resume '
          + 'to. Delete it and create another; if that fails too, the error will be the same one '
          + 'and worth reporting.',
      }

    case 'paused':
      return {
        tone: 'info',
        title: 'Paused',
        body: 'You paused this project. Its data is kept and its keys are refused until you '
          + 'resume it.',
      }

    case 'suspended':
      return {
        tone: 'warn',
        title: 'Suspended',
        body: 'This project was suspended by the platform, not by you. Its data is kept and its '
          + 'keys are refused. Resuming it is not something you can do from here.',
      }

    case 'deleting':
      return {
        tone: 'warn',
        title: 'Being deleted',
        body: 'Its provider resources are still being removed. Nothing here will work again.',
      }

    case 'deleted':
      return {
        tone: 'warn',
        title: 'Deleted',
        body: 'This project and everything in it is gone.',
      }
  }
}
